// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import cx from 'classnames';
import css from '../shared/css';
import Menu from '../shared/menu';
import {
  AvatarOrgIcon,
  PlusIcon,
  PowerIcon,
} from '../icons';
import {
  useSession,
  useOrganization,
  useMemberships,
  useSessionUser,
} from '../contexts/session-context';
import { useConfig } from '../contexts/config-context';
import ArcGISLogout from './arcgis/logout';

const contextPath = (membershipId: string) => `/context/${membershipId}`;

export const OrgLinks = () => {
  const { esriApiKey } = useConfig();
  const { connectors } = useOrganization();

  return (
    <>
      <a className={cx(styles.dropdownItem, styles.dropdownItemNewOrg)} href="/organizations/new">
        <PlusIcon style={{ fill: '#ffc90f' }} className={cx(styles.menuIcon, styles.newOrgIcon)} width={12} />
        Create New Organization
      </a>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <a
        className={cx(styles.dropdownItem, styles.dropdownItemSignout)}
        onClick={(event) => {
          event.preventDefault();
          ArcGISLogout(esriApiKey, connectors, () => window.location.assign('/users/sign_out'));
        }}
        href="/users/sign_out"
      >
        <PowerIcon style={{ fill: '#bbb' }} className={cx(styles.menuIcon, styles.signOutIcon)} width={14} />
        Sign Out
      </a>
    </>
  );
};

type OrgButtonProps = {
  icon: React.ReactNode;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export const OrgButton = ({
  icon,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: OrgButtonProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <span
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon}
    </span>
  );
};

type OrgHoverProps = {
  name: string;
  email: string;
};

export const OrgHover = ({ name, email }: OrgHoverProps) => (
  <div className={styles.orgHoverContainer}>
    <div className={styles.orgHoverArrow} />
    <span className={styles.orgHoverInfo}>
      <p className={cx(styles.orgHoverItem, 'subheading')}>{name}</p>
      <p className={cx(styles.orgHoverEmail, styles.orgHoverItem, 'utility')}>{email}</p>
    </span>
  </div>
);

export const formatMemberships = (
  organizationId, memberships, esriApiKey, connectors,
) => memberships.map((membership) => {
  const isCurrent = organizationId === membership.id;
  return {
    label: membership.name,
    onClick: isCurrent
      ? undefined
      : () => ArcGISLogout(
        esriApiKey,
        connectors,
        () => window.open(contextPath(membership.membershipId), '_self'),
      ),
    icon: () => (
      membership.imageSmall
        ? <img className={cx(styles.orgMenuIcon, styles.menuIcon)} src={membership.imageSmall} alt={membership.name} />
        : <AvatarOrgIcon className={styles.orgMenuIcon} width={20} />
    ),
    current: isCurrent,
  };
});

export const GlobalNavOrg = () => {
  const [toggleOrg, setToggleOrg] = useState(false);
  const [orgHover, setOrgHover] = useState(false);

  const { esriApiKey } = useConfig();
  const { isAdminLoginAs } = useSession();
  const organization = useOrganization();
  const memberships = useMemberships();
  const sessionUser = useSessionUser();

  const currentContext = memberships.find((membership) => organization.id === membership.id);

  const { connectors } = organization;

  const membershipsToShow = isAdminLoginAs
    ? memberships.filter((m) => m.id === organization.id)
    : memberships;

  const items = formatMemberships(
    organization.id,
    membershipsToShow,
    esriApiKey,
    connectors,
  );

  const escFunction = useCallback((event) => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      setToggleOrg(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  return (
    <>
      <OrgButton
        icon={
          currentContext?.imageSmall
            ? (
              <img
                className={cx(styles.actionIcons, styles.orgIcon)}
                src={currentContext.imageSmall}
                alt={currentContext.name}
              />
            )
            : <AvatarOrgIcon className={cx(styles.actionIcons, styles.orgIcon)} width={30} />
        }
        onClick={() => setToggleOrg(!toggleOrg)}
        onMouseEnter={() => setOrgHover(true)}
        onMouseLeave={() => setOrgHover(false)}
      />
      <Menu
        isOrgMenu
        show={toggleOrg}
        onHide={() => setToggleOrg(false)}
        items={items}
        style={{ width: 250 }}
        className={styles.dropDownNew}
        verticalOffset={6}
        horizontalOffset={-220}
        title="Account"
        minWidth={255}
      />
      {orgHover && currentContext && <OrgHover name={currentContext.name} email={sessionUser.email} />}
    </>
  );
};

export default GlobalNavOrg;

const styles = css({
  dropDownNew: {
    padding: '5px 0',
  },
  menuIcon: {
    width: 20,
    marginRight: 5,
    display: 'flex',
    justifyContent: 'center',
  },
  orgMenuIcon: {
    height: 20,
    width: 20,
    minWidth: 20,
    marginRight: 5,
    borderRadius: 12,
    fill: '#bbb',
  },
  orgHoverContainer: {
    borderRadius: 5,
    backgroundColor: '#fff',
    border: 'none',
    boxShadow: '0 1px 12px 0 rgba(0,0,0,0.2)',
    position: 'absolute',
    top: 2,
    right: 65,
    zIndex: 9,
  },
  orgHoverArrow: {
    right: 0,
    content: '" "',
    height: 14,
    width: 14,
    position: 'absolute',
    pointerEvents: 'none',
    backgroundColor: '#fff',
    top: 14,
    marginRight: -7,
    transform: 'rotate(45deg)',
    transformOrigin: '50% 50%',
    boxShadow: '0 1px 12px 0 rgba(0,0,0,0.2)',
    zIndex: 4,
  },
  orgHoverInfo: {
    borderRadius: 5,
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    padding: '6px 15px',
    position: 'relative',
    zIndex: 5,
  },
  orgHoverItem: {
    margin: 0,
    padding: 0,
    fontSize: 12,
  },
  orgHoverEmail: {
    color: '#7d7d7d',
    fontSize: 11,
  },
  dropdownItem: {
    color: '#41403b',
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    padding: '5px 15px',
    margin: '2px 0',

    ':hover': {
      background: '#f4f4f4',
      color: '#41403b',
    },
  },
  dropdownItemNewOrg: {
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    margin: '5px 0',
    paddingTop: 10,
    paddingBottom: 10,

    ':hover': {
      background: '#fffae7',
    },
  },
  actionIcons: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fill: '#7d7d7d',

    ':hover': {
      fill: '#41403b',
    },
  },
  orgIcon: {
    cursor: 'pointer',
    width: 30,
    height: 30,
    borderRadius: 18,
    padding: 2,

    ':hover': {
      boxShadow: '0 1px 12px 0 rgba(0,0,0,0.2)',
    },
  },
});
